import currentTimeZone from '../config/timeZone';

type MonthNameDataBase = { monthName: string };
type MonthDataBase = { month: string };
type DateDataBase = { date: string };

/**
 * Determines the chart aggregation bucket (hour/day/month) and day span for an
 * arbitrary custom date range, mirroring the granularity previously implied by
 * the fixed 'last24hours' | 'last7days' | 'lastMonth' | 'last12months' keys.
 * @param {string} startDate - Range start date (YYYY-MM-DD)
 * @param {string} endDate - Range end date (YYYY-MM-DD)
 * @returns {{ bucket: 'hour' | 'day' | 'month'; days: number }} The bucket type and number of days spanned
 */
export function getBucketFromRange(
  startDate: string,
  endDate: string,
): { bucket: 'hour' | 'day' | 'month'; days: number } {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays =
    Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())
      ? 1
      : Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);

  if (diffDays <= 1) return { bucket: 'hour', days: diffDays };
  if (diffDays <= 31) return { bucket: 'day', days: diffDays };
  return { bucket: 'month', days: diffDays };
}

/**
 * Fills missing months in the last 12 months and sets missing numeric fields to 0.
 * Adds a `label` field formatted as `Month Year` (e.g., "Jan 2024").
 * @param {any} data - The data
 * @param {any} locale - The locale
 */
export function fillMissingMonthsGeneric<T extends MonthDataBase | MonthNameDataBase>(
  data: T[],
  locale: string = 'en-US',
): (Omit<T, 'monthName' | 'month'> & { label: string })[] {
  const today = new Date();
  const dateFormatter = new Intl.DateTimeFormat(locale, { month: 'short' });
  const yearFormatter = new Intl.DateTimeFormat(locale, { year: '2-digit' });
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const past12Months = [];
  for (let index = 0; index < 12; index += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1);
    const key = months[date.getMonth()];
    const label = dateFormatter.format(date);
    const year = yearFormatter.format(date);
    past12Months.push({ key, fullLabel: `${label} ${year}` });
  }

  const dataMap = new Map<string, T>();
  data.forEach((item) => {
    const key = (item as any).monthName ?? (item as any).month;
    if (key) dataMap.set(key, item);
  });

  // Get all unique keys (except monthName) to fill missing fields
  const fieldKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'monthName' && key !== 'month') fieldKeys.add(key);
    });
  });

  const result = past12Months.map(({ key, fullLabel }) => {
    const existing = dataMap.get(key);
    if (existing) {
      const { monthName, month, ...rest } = existing as any;
      return { ...rest, label: fullLabel };
    }

    const filled: Record<string, any> = { label: fullLabel };
    fieldKeys.forEach((k) => {
      filled[k] = 0;
    });

    return filled as Omit<T, 'monthName' | 'month'> & { label: string };
  });

  return result.reverse();
}

/**
 * Fills missing days in the past 30 days, formatting the label as "MMM DD" (e.g., "May 08"),
 * and filling missing numeric fields with 0.
 * Assumes input `label` is an ISO date string.
 * @param {any} data - The data
 * @param {any} nDays - The nDays
 * @param {any} locale - The locale
 */
export function fillMissingLastNDaysGeneric<T extends DateDataBase>(
  data: T[],
  nDays: number = 30,
  locale: string = 'en-US',
): (Omit<T, 'date'> & { label: string })[] {
  const today = new Date();
  const past30Days: Date[] = [];

  for (let index = 0; index < nDays; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    past30Days.push(date);
  }

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  });

  // Lookup map for quick access
  const dataMap = new Map<string, T>();
  data.forEach((item) => {
    const iso = item.date.split('T')[0];
    dataMap.set(iso, item);
  });

  // Collect all keys except 'date'
  const fieldKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'date') fieldKeys.add(key);
    });
  });

  const result = past30Days.map((date) => {
    const isoDate = date.toISOString().split('T')[0];
    const existing = dataMap.get(isoDate);

    if (existing) {
      const { date: discardedDate, ...rest } = existing;
      return { ...rest, label: dateFormatter.format(date) };
    }

    const defaultItem: Record<string, any> = { label: dateFormatter.format(date) };
    fieldKeys.forEach((key) => {
      defaultItem[key] = 0;
    });

    return defaultItem as Omit<T, 'date'> & { label: string };
  });

  return result.reverse();
}

/**
 * Fills missing data for the past 24 hours with default values (0), grouped by hour and date.
 * @param {any} data Input data containing at least `date` (YYYY-MM-DD) and `hour` (0–23)
 * @param {any} timeZone Optional time zone (defaults to browser's currentTimeZone)
 * @param {any} locale - The locale
 * @returns {any} Array of data objects, each with `label`, `hour`, and all other fields (defaulted to 0 if missing)
 */
export function fillMissingLast24HrsGeneric<T extends { date: string; hour: number | string }>(
  data: T[],
  timeZone: string = currentTimeZone,
  locale: string = 'en-US',
): Array<Omit<T, 'date' | 'hour'> & { label: string }> {
  const now = new Date();
  const past24Hours: { date: string; hour: number }[] = [];

  for (let index = 0; index < 24; index += 1) {
    const d = new Date(now);
    d.setHours(now.getHours() - index, 0, 0, 0);

    const dateString = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);

    past24Hours.push({ date: dateString, hour: d.getHours() });
  }

  const dataMap = new Map(data.map((item) => [`${item.date}_${item.hour}`, item]));

  const allKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'date' && key !== 'hour') allKeys.add(key);
    });
  });

  const hourFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    hour12: false,
  });

  const result = past24Hours.map(({ date, hour }) => {
    const key = `${date}_${hour}`;
    const existing = dataMap.get(key);

    // Create a date object to format the hour label according to locale
    const temporaryDate = new Date();
    temporaryDate.setHours(hour, 0, 0, 0);
    const label = hourFormatter.format(temporaryDate);

    if (existing) {
      const { date: unusedDate, hour: unusedHour, ...rest } = existing;
      return { label, ...rest } as any;
    }

    const fallback: Record<string, any> = { label };
    allKeys.forEach((k) => {
      fallback[k] = 0;
    });

    return fallback;
  });

  return result.reverse();
}

/**
 * Fills missing data for the past N days with default values (0).
 * @param {any} data Input data containing at least `date` (YYYY-MM-DD)
 * @param {any} nDays Number of days to fill (defaults to 30)
 * @param {any} locale Optional locale (defaults to 'en-US')
 * @returns {any} Array of data objects, each with `label` and all other fields (defaulted to 0 if missing)
 */
export function fillMissingLastNDaysGenericLatest<T extends DateDataBase>(
  data: T[],
  nDays: number = 30,
  locale: string = 'en-US',
): (Omit<T, 'date'> & { label: string })[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pastNDays: Date[] = [];

  for (let index = 0; index < nDays; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    pastNDays.push(date);
  }

  const startDate = new Date(today);
  startDate.setDate(today.getDate() - (nDays - 1));

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  });

  const dataMap = new Map<string, T>();

  data.forEach((item) => {
    if (!item.date) return; // ✅ Skip null dates

    const itemDate = new Date(item.date);
    itemDate.setHours(0, 0, 0, 0);

    // ✅ Filter only within required range
    if (itemDate >= startDate && itemDate <= today) {
      const iso = itemDate.toISOString().split('T')[0];
      dataMap.set(iso, item);
    }
  });

  const fieldKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== 'date') fieldKeys.add(key);
    });
  });

  const result = pastNDays.map((date) => {
    const isoDate = date.toISOString().split('T')[0];
    const existing = dataMap.get(isoDate);

    if (existing) {
      const { date: discardedDate, ...rest } = existing;
      return { ...rest, label: dateFormatter.format(date) };
    }

    const defaultItem: Record<string, any> = { label: dateFormatter.format(date) };

    fieldKeys.forEach((key) => {
      defaultItem[key] = 0;
    });

    return defaultItem as Omit<T, 'date'> & { label: string };
  });

  return result.reverse();
}
